import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { downloadResponsesCsv } from '../lib/csv-download.js';
import { apiPost, apiPatch, apiDelete } from '../lib/api-mutations.js';
import { useToast } from '../hooks/useToast.js';
import { ProcessingStatus } from '../components/ProcessingStatus.js';
import { PromptForm } from '../components/PromptForm.js';
import type { PromptFormData } from '../components/PromptForm.js';
import type { PromptTemplate, PromptTemplateListResponse, PromptSuggestion, PromptSuggestionListResponse } from '../types/index.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Select } from '../components/Select.js';
import { Tabs } from '../components/Tabs.js';
import { EmptyState } from '../components/EmptyState.js';
import type { TabItem } from '../components/Tabs.js';
import { RowList, Row, RowFacts, RowFact } from '../components/ui/Row.js';
import { Chip } from '../components/ui/Chip.js';
import type { ChipProps } from '../components/ui/Chip.js';

type PromptTab = 'active' | 'suggestions';

const PROMPT_TABS: readonly TabItem<PromptTab>[] = [
  { id: 'active', label: 'Active prompts', icon: 'bx-file-blank' },
  { id: 'suggestions', label: 'Suggestions', icon: 'bx-bulb' },
] as const;

// Spec 235 task 3 — the chip-tone mapping (evidence.md): yellow -> warn,
// blue -> info, red -> fail, green -> ok. Each tone still carries its own
// text label (the status word), never a bare colour.
const STATUS_TONE: Record<string, ChipProps['tone']> = {
  pending: 'warn',
  approved: 'info',
  rejected: 'fail',
  converted: 'ok',
};

function StatusBadge({ status }: { status: string }) {
  return <Chip tone={STATUS_TONE[status]}>{status}</Chip>;
}

export function PromptsPage() {
  const { selectedBrand, user } = useBrand();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<PromptTab>('active');
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDiscovery, setLastDiscovery] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [topics, setTopics] = useState<Array<{ id: string; name: string; prompt_count: number }>>([]);
  const [topicFilter, setTopicFilter] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Fetch active templates — backend route is /api/v1/tenants/:tenantId/prompts
  const fetchTemplates = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/prompts?limit=100&is_active=true&include_metrics=true&brand_id=${selectedBrand.id}`;
      const result = await apiGet<PromptTemplateListResponse>(url);
      setTemplates(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/prompts/suggestions?limit=100`;
      const result = await apiGet<PromptSuggestionListResponse>(url);
      setSuggestions(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user]);

  // Fetch topics (063)
  const fetchTopics = useCallback(async () => {
    if (!selectedBrand || !user) return;
    try {
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/topics`;
      const result = await apiGet<{ data: Array<{ id: string; name: string; prompt_count: number }> }>(url);
      setTopics(result.data);
    } catch {
      // Topics are optional, ignore errors
    }
  }, [selectedBrand, user]);

  useEffect(() => {
    if (tab === 'active') {
      fetchTemplates();
      fetchTopics();
    } else {
      fetchSuggestions();
    }
  }, [tab, fetchTemplates, fetchSuggestions, fetchTopics]);

  const handleApprove = async (id: string) => {
    try {
      await apiFetch(`/api/v1/prompts/suggestions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'approved' }),
      });
      fetchSuggestions();
    } catch {
      setError('Failed to approve suggestion');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await apiFetch(`/api/v1/prompts/suggestions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'rejected' }),
      });
      fetchSuggestions();
    } catch {
      setError('Failed to reject suggestion');
    }
  };

  const handleConvert = async (id: string) => {
    try {
      await apiFetch(`/api/v1/prompts/suggestions/${id}/convert`, { method: 'POST' });
      fetchSuggestions();
      fetchTemplates();
    } catch {
      setError('Failed to convert suggestion');
    }
  };

  const handleCreatePrompt = async (data: PromptFormData) => {
    if (!user || !selectedBrand) return;
    await apiPost(`/api/v1/tenants/${selectedBrand.tenant_id}/prompts`, {
      template: data.template,
      category: data.category,
      locale: data.locale,
      brand_id: selectedBrand?.id,
    });
    addToast('Prompt created successfully', 'success');
    setShowForm(false);
    fetchTemplates();
  };

  const handleUpdatePrompt = async (data: PromptFormData) => {
    if (!user || !editingPrompt || !selectedBrand) return;
    await apiPatch(`/api/v1/tenants/${selectedBrand.tenant_id}/prompts/${editingPrompt.id}`, {
      template: data.template,
      category: data.category,
      locale: data.locale,
    });
    addToast('Prompt updated successfully', 'success');
    setEditingPrompt(null);
    fetchTemplates();
  };

  const handleExportPrompt = async (promptId: string) => {
    if (!selectedBrand) return;
    try {
      await downloadResponsesCsv(
        selectedBrand.tenant_id,
        selectedBrand.id,
        new URLSearchParams({ prompt_template_id: promptId }),
      );
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to export responses', 'error');
    }
  };

  const handleDeletePrompt = async (id: string) => {
    if (!user || !selectedBrand) return;
    try {
      await apiDelete(`/api/v1/tenants/${selectedBrand.tenant_id}/prompts/${id}`);
      addToast('Prompt deleted', 'success');
      setDeleteConfirm(null);
      fetchTemplates();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete prompt', 'error');
    }
  };

  // Bulk selection (077) — matches the list actually rendered below
  const visibleTemplates = templates;

  useEffect(() => {
    if (selectAllRef.current) {
      const count = selectedIds.size;
      selectAllRef.current.indeterminate = count > 0 && count < visibleTemplates.length;
    }
  }, [selectedIds, visibleTemplates.length]);

  // Clear selection whenever the tab changes or the visible set shrinks
  useEffect(() => {
    if (tab !== 'active') setSelectedIds(new Set());
  }, [tab]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visibleTemplates.length && visibleTemplates.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleTemplates.map((t) => t.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!user || !selectedBrand || selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map((id) => apiDelete(`/api/v1/tenants/${selectedBrand.tenant_id}/prompts/${id}`)),
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;
    if (failed === 0) {
      addToast(`Deleted ${succeeded} of ${ids.length} prompts`, 'success');
    } else if (succeeded === 0) {
      addToast('Failed to delete prompts', 'error');
    } else {
      addToast(`Deleted ${succeeded}, ${failed} failed`, 'error');
    }
    setBulkDeleting(false);
    setBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    fetchTemplates();
  };

  const handleRunDiscovery = async () => {
    if (!selectedBrand || !user) return;
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/prompts/discover`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(errBody?.error ?? `Discovery failed (${res.status})`);
      }
      const result = await res.json() as { suggestions_created?: number; mode?: string };
      const count = result.suggestions_created ?? 0;
      setLastDiscovery(new Date().toISOString());
      addToast(`${count} suggestion${count !== 1 ? 's' : ''} generated (${result.mode ?? 'live'} mode)`, 'success');
      await fetchSuggestions();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Discovery failed', 'error');
    }
  };

  if (!selectedBrand) {
    return (
      <div className="hub-page--table">
        <h1 className="hub-page__title">Prompts</h1>
        <div>
          <EmptyState
            icon="bx-bulb"
            title="Select a brand"
            description="Choose a brand from the selector above to manage monitoring prompts."
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="prompts-page" className="hub-page--table">
      {/* Spec 246 (review P0-1): the archetype owns the padding, so the title
          no longer starts on the sidebar's hairline. Title and subtitle share a
          head, or the archetype's flex gap would pull them apart. */}
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Prompts</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">Manage monitoring prompts for {selectedBrand.name}</p>
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="prompt-tabs">
        <Tabs tabs={PROMPT_TABS} activeTab={tab} onTabChange={setTab} ariaLabel="Prompt tabs" />
      </div>

      {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="mt-8 flex justify-center"><Spinner className="text-violet-600" /></div>}

      {/* Active Templates Tab. The testid names the LIST, so it lives on
          RowList below, not on this wrapper — which also holds the topic
          filters and the bulk-action bar, neither of which is the list. */}
      {!loading && tab === 'active' && (
        <div className="mt-4">
          <div className="flex items-end justify-between gap-3">
            {/* Topic filter (063). Spec 249: it was a row of pills acting as a
                filter toggle, which a Kridt chip never is — a labelled select,
                as Responses and Domains filter. */}
            {topics.length > 0 ? (
              <div className="hub-field" data-testid="topic-filters">
                <label className="hub-field__label" htmlFor="prompts-topic">Topic</label>
                <Select
                  id="prompts-topic"
                  options={[
                    { value: '', label: `All topics (${templates.length})` },
                    ...topics.map((tp) => ({ value: tp.id, label: `${tp.name} (${tp.prompt_count})` })),
                  ]}
                  value={topicFilter}
                  onChange={setTopicFilter}
                  placeholder="All topics"
                  className="w-56"
                />
              </div>
            ) : <span />}
            <button
              onClick={() => setShowForm(true)}
              className="hub-btn hub-btn--primary"
              data-testid="add-prompt-btn"
            >
              <Icon name="bx-plus" />
              Add prompt
            </button>
          </div>
          {templates.length === 0 && (
            <div className="mt-3">
              <EmptyState
                icon="bx-file-blank"
                title="No active prompts"
                description="Create prompts or approve suggestions to start monitoring."
                variant="compact"
              />
            </div>
          )}

          {/* Select-all + bulk action bar (077) */}
          {templates.length > 0 && (
            <div className="mt-3 flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-2">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={selectedIds.size === visibleTemplates.length && visibleTemplates.length > 0}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded-md border-slate-300 text-violet-600 focus:ring-violet-500"
                  data-testid="prompt-select-all"
                  aria-label="Select all prompts"
                />
                Select all
              </label>
              {selectedIds.size > 0 && (
                <div className="flex flex-1 items-center justify-between gap-3">
                  <span className="text-xs text-slate-600" data-testid="bulk-selection-count">
                    {selectedIds.size} prompt{selectedIds.size === 1 ? '' : 's'} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      disabled={bulkDeleting}
                      className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                      data-testid="bulk-clear-btn"
                    >
                      Clear selection
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkDeleteConfirm(true)}
                      disabled={bulkDeleting}
                      className="hub-btn hub-btn--danger"
                      data-testid="bulk-delete-btn"
                    >
                      {bulkDeleting ? <Spinner  /> : <Icon name="bx-trash" />}
                      Delete {selectedIds.size}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {templates.length > 0 && (
            <RowList className="mt-3" data-testid="active-templates-list">
              {templates.map((t) => (
                // Spec 235 — "View responses" was one of four icon buttons;
                // it is also the one genuine per-row destination this list
                // has, so it becomes the row's own stretched link, and the
                // button stays too (same destination) rather than dropping
                // its testid. The checkbox lives in `lead`, which sits
                // outside .hub-row__body but still inside the SAME
                // positioned .hub-row the stretched ::after covers — so it
                // needs .hub-row__interactive exactly like the export/edit/
                // delete buttons in `actions` do.
                <Row
                  key={t.id}
                  to={`/responses?prompt_template_id=${t.id}`}
                  data-testid={`template-${t.id}`}
                  lead={
                    <input
                      type="checkbox"
                      checked={selectedIds.has(t.id)}
                      onChange={() => toggleSelect(t.id)}
                      className="hub-row__interactive h-4 w-4 rounded-md border-slate-300 text-violet-600 focus:ring-violet-500"
                      data-testid={`prompt-select-${t.id}`}
                      aria-label={`Select prompt ${t.template.slice(0, 40)}`}
                    />
                  }
                  title={<span title={t.template}>{t.template.length > 120 ? t.template.slice(0, 120) + '...' : t.template}</span>}
                  top={
                    <>
                      <span className="hub-tag">{t.category.replace(/_/g, " ")}</span>
                      {t.is_seeded && <span className="hub-tag">Seeded</span>}
                    </>
                  }
                  actions={
                    <>
                      <button
                        onClick={() => navigate(`/responses?prompt_template_id=${t.id}`)}
                        className="hub-row__interactive hub-btn hub-btn--quiet hub-btn--icon"
                        title="View responses"
                        aria-label="View responses"
                        data-testid={`view-responses-${t.id}`}
                      >
                        <Icon name="bx-message" />
                      </button>
                      <button
                        onClick={() => handleExportPrompt(t.id)}
                        className="hub-btn hub-btn--quiet hub-btn--icon"
                        title="Export CSV"
                        aria-label="Export prompt as CSV"
                        data-testid={`export-prompt-${t.id}`}
                      >
                        <Icon name="bx-download" />
                      </button>
                      <button
                        onClick={() => setEditingPrompt(t)}
                        className="hub-btn hub-btn--quiet hub-btn--icon"
                        title="Edit prompt"
                        aria-label="Edit prompt"
                        data-testid={`edit-prompt-${t.id}`}
                      >
                        <Icon name="bx-pencil" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(t.id)}
                        className="hub-btn hub-btn--quiet hub-btn--icon"
                        title="Delete prompt"
                        aria-label="Delete prompt"
                        data-testid={`delete-prompt-${t.id}`}
                      >
                        <Icon name="bx-trash" />
                      </button>
                    </>
                  }
                >
                  {/* Performance Metrics (Spec 047) */}
                  {t.metrics && (
                    <RowFacts data-testid={`metrics-${t.id}`}>
                      <RowFact>
                        <span className={
                          t.metrics.mention_rate !== null
                            ? t.metrics.mention_rate > 0.5 ? 'text-green-600' : t.metrics.mention_rate > 0.2 ? 'text-yellow-600' : 'text-red-600'
                            : 'text-gray-400'
                        }>
                          {t.metrics.mention_rate !== null ? `${(t.metrics.mention_rate * 100).toFixed(0)}%` : '--'} mention
                        </span>
                      </RowFact>
                      <RowFact>
                        {t.metrics.avg_sentiment !== null ? (
                          <span className={t.metrics.avg_sentiment > 0 ? 'text-green-600' : t.metrics.avg_sentiment < 0 ? 'text-red-600' : 'text-gray-600'}>
                            {t.metrics.avg_sentiment > 0 ? '+' : ''}{t.metrics.avg_sentiment.toFixed(2)} sentiment
                          </span>
                        ) : '-- sentiment'}
                      </RowFact>
                      <RowFact>{t.metrics.sample_count} samples</RowFact>
                      {t.metrics.last_run_at && (
                        <RowFact>Last run {new Date(t.metrics.last_run_at).toLocaleDateString()}</RowFact>
                      )}
                    </RowFacts>
                  )}
                </Row>
              ))}
            </RowList>
          )}
        </div>
      )}

      {/* Create/Edit Prompt Form Modal */}
      {showForm && selectedBrand && (
        <PromptForm
          tenantId={selectedBrand.tenant_id}
          onSubmit={handleCreatePrompt}
          onCancel={() => setShowForm(false)}
        />
      )}
      {editingPrompt && selectedBrand && (
        <PromptForm
          tenantId={selectedBrand.tenant_id}
          initial={{
            template: editingPrompt.template,
            category: editingPrompt.category,
            locale: editingPrompt.locale ?? 'en',
          }}
          onSubmit={handleUpdatePrompt}
          onCancel={() => setEditingPrompt(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-confirm-dialog">
          <div className="w-full max-w-sm rounded-xl border border-[var(--hub-color-line)] bg-white p-6">
            <h3 className="text-base font-semibold text-slate-900">Delete prompt</h3>
            <p className="mt-2 text-sm text-slate-600">Are you sure you want to delete this prompt template? This action cannot be undone.</p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePrompt(deleteConfirm)}
                className="hub-btn hub-btn--danger"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog (077) */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bulk-delete-confirm-dialog">
          <div className="w-full max-w-sm rounded-xl border border-[var(--hub-color-line)] bg-white p-6">
            <h3 className="text-base font-semibold text-slate-900">Delete {selectedIds.size} prompt{selectedIds.size === 1 ? '' : 's'}</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete {selectedIds.size} prompt{selectedIds.size === 1 ? '' : 's'}? This action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setBulkDeleteConfirm(false)}
                disabled={bulkDeleting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="hub-btn hub-btn--danger"
                data-testid="confirm-bulk-delete-btn"
              >
                {bulkDeleting && <Spinner  />}
                Delete {selectedIds.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Tab. The testid names the LIST, so it lives on RowList
          below, not on this wrapper — which also holds ProcessingStatus. */}
      {!loading && tab === 'suggestions' && (
        <div className="mt-4">
          <ProcessingStatus
            label="Prompt discovery"
            lastRun={lastDiscovery ?? (suggestions.length > 0 ? suggestions[0].suggested_at : null)}
            count={suggestions.length}
            countLabel="suggestions"
            status={suggestions.length > 0 ? 'complete' : 'never'}
            onRun={handleRunDiscovery}
            runLabel="Run discovery"
          />
          {suggestions.length === 0 && (
            <div className="mt-3">
              <EmptyState
                icon="bx-bulb"
                title="No suggestions yet"
                description="Run prompt discovery above to generate suggestions."
                variant="compact"
              />
            </div>
          )}
          {suggestions.length > 0 && (
            <RowList className="mt-3" data-testid="suggestions-list">
              {suggestions.map((s) => (
                // Suggestions have no detail page of their own — disclosure
                // shape, no `to`. Every action already lives in `actions`,
                // outside the (absent) overlay, so none needs a z-index lift.
                <Row
                  key={s.id}
                  data-testid={`suggestion-${s.id}`}
                  title={s.suggested_prompt}
                  top={<StatusBadge status={s.status} />}
                  actions={
                    <>
                      {s.status === 'pending' && (
                        <>
                          <button onClick={() => handleApprove(s.id)} className="rounded-md p-1 text-green-600 hover:bg-green-50" title="Approve">
                            <Icon name="bx-check" />
                          </button>
                          <button onClick={() => handleReject(s.id)} className="rounded-md p-1 text-red-600 hover:bg-red-50" title="Reject">
                            <Icon name="bx-x" />
                          </button>
                        </>
                      )}
                      {s.status === 'approved' && (
                        <button onClick={() => handleConvert(s.id)} className="rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100" data-testid={`convert-${s.id}`}>
                          Convert
                        </button>
                      )}
                      {s.status === 'rejected' && (
                        <button
                          onClick={async () => {
                            try {
                              await apiFetch(`/api/v1/prompts/suggestions/${s.id}`, { method: 'DELETE' });
                              addToast('Suggestion deleted', 'success');
                              fetchSuggestions();
                            } catch {
                              addToast('Failed to delete suggestion', 'error');
                            }
                          }}
                          className="rounded-md p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                          title="Delete rejected suggestion"
                        >
                          <Icon name="bx-trash" />
                        </button>
                      )}
                    </>
                  }
                >
                  <RowFacts>
                    {s.category && <RowFact>{s.category}</RowFact>}
                    <RowFact>{s.source}</RowFact>
                    {s.ai_search_volume !== null && <RowFact>AI vol: {s.ai_search_volume}</RowFact>}
                  </RowFacts>
                </Row>
              ))}
            </RowList>
          )}
        </div>
      )}
    </div>
  );
}
